import { __awaiter } from '../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, useCallback, useRef, useEffect } from 'react';
import { toast, Toaster } from 'sonner';
import { ArrowLeft, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useBrandAppConfig } from '../api/cache/appConfigQueryOptions.js';
import { getHeadersWithUniqueVisitorId } from '../Auth/index.js';
import { HistoryList, ConciergeHeaderWidget } from '@brandclub/bc-widgets';
import { ConciergeChatAreaV2 } from './components/ConciergeChatAreaV2.js';
import { useConciergeContext } from './ConciergeContextProvider.js';
import { useAutoScroll } from './hooks/useAutoScroll.js';
import { useConversationLoader } from './hooks/useConversationLoader.js';
import { usePageContext } from './hooks/usePageContext.js';
import { useStreamingSearch } from './hooks/useStreamingSearch.js';
import { useConciergeTracking } from './hooks/useConciergeTracking.js';
import { useConciergePendingQuery } from './stores/useConciergePendingQuery.js';
import { wrapSearchWithProductContext } from './utils/wrapSearchWithProductContext.js';

// API function to fetch conversations
const fetchConversations = (apiBaseUrl) => __awaiter(void 0, void 0, void 0, function* () {
    const headers = yield getHeadersWithUniqueVisitorId();
    // apiBaseUrl already includes /api-rewards for checkout-shop-app
    // For landing-page, apiBaseUrl is empty so we need the full path
    const baseUrl = apiBaseUrl || '/api-rewards';
    const response = yield fetch(`${baseUrl}/concierge/conversations`, {
        method: 'GET',
        headers: Object.assign({ 'Content-Type': 'application/json' }, headers),
    });
    if (!response.ok) {
        throw new Error('Failed to fetch conversations');
    }
    return response.json();
});
const ConciergeWidgetChatContent = ({ pageContext: providedPageContext, widgetState, showMaximize = true, isMaximized = false, onMaximize, onMinimize, onVariantSelect, title, initialQuery, initialQueryStacklineSku, onInitialQueryProcessed, brandId: propBrandId, }) => {
    var _a, _b;
    // Get conversation state from context (includes apiBaseUrl for checkout-shop-app)
    const conversationState = useConciergeContext();
    // Brand config
    const { data: config } = useBrandAppConfig(conversationState.apiBaseUrl);
    const params = new URLSearchParams(window.location.search);
    // Priority: prop > URL param > brand config
    const brandId = (propBrandId === null || propBrandId === void 0 ? void 0 : propBrandId.toString()) || params.get('brandId') || ((_b = (_a = config === null || config === void 0 ? void 0 : config.brandIds) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.toString()) || null;
    const appConfigId = params.get('appConfigId') || (config === null || config === void 0 ? void 0 : config.appConfigId) || null;
    // Input state
    const [searchInputValue, setSearchInputValue] = useState('');
    // History view state
    const [showHistory, setShowHistory] = useState(false);
    // Fetch conversations for history view
    const { data: conversationsData, isLoading: isLoadingConversations, error: conversationsError, } = useQuery({
        queryKey: ['conversations', conversationState.apiBaseUrl],
        queryFn: () => fetchConversations(conversationState.apiBaseUrl),
        enabled: showHistory,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
    // Handle history icon click
    const handleHistoryClick = useCallback(() => {
        setShowHistory(true);
    }, []);
    // Handle back from history view
    const handleHistoryBack = useCallback(() => {
        setShowHistory(false);
    }, []);
    // Handle conversation selection from history
    const handleHistorySelect = useCallback((selectedConversationId) => {
        conversationState.setConversationId(selectedConversationId);
        // Clear existing conversation state to trigger reload
        conversationState.setConversationHistory([]);
        conversationState.setProductMetadataMap({});
        conversationState.setFollowUpQuestions([]);
        conversationState.setPreviewSearchQueries([]);
        conversationState.setPreviewQueryProducts({});
        setShowHistory(false);
    }, [conversationState]);
    // Handle new conversation creation
    const handleNewConversation = useCallback(() => {
        // Clear conversation state to start fresh
        conversationState.setConversationId(null);
        conversationState.setConversationHistory([]);
        conversationState.setProductMetadataMap({});
        conversationState.setFollowUpQuestions([]);
        conversationState.setPreviewSearchQueries([]);
        conversationState.setPreviewQueryProducts({});
        setShowHistory(false);
    }, [conversationState]);
    // Get page context - use provided one or fallback to hook
    // usePageContext will work even without react-router (uses window.location)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const hookPageContext = usePageContext();
    const pageContext = providedPageContext !== null && providedPageContext !== void 0 ? providedPageContext : hookPageContext;
    // Pending access point ref - stores the access point for the next message
    // This allows us to track where a conversation was started from
    const pendingAccessPointRef = useRef(null);
    // Track if this is a new conversation (no existing messages)
    const isNewConversationRef = useRef(true);
    useEffect(() => {
        isNewConversationRef.current = conversationState.conversationHistory.length === 0;
    }, [conversationState.conversationHistory.length]);
    // Initialize tracking hook
    const { trackConversationStart, trackPageContext } = useConciergeTracking({
        conversationId: conversationState.conversationId,
        pageContext,
    });
    // Track when a new conversationId is received (conversation started)
    const previousConversationIdRef = useRef(null);
    useEffect(() => {
        const currentId = conversationState.conversationId;
        const previousId = previousConversationIdRef.current;
        // If we have a new conversation ID and a pending access point, track it
        if (currentId && currentId !== previousId && pendingAccessPointRef.current) {
            const { accessPoint, query } = pendingAccessPointRef.current;
            const isNew = previousId === null;
            // Track the conversation access point (non-blocking)
            trackConversationStart(accessPoint, query, isNew);
            // Clear the pending access point
            pendingAccessPointRef.current = null;
        }
        previousConversationIdRef.current = currentId;
    }, [conversationState.conversationId, trackConversationStart]);
    // Callback to track access point - called by child components
    const handleTrackAccessPoint = useCallback((accessPoint, query) => {
        // Store the access point to be tracked when we get a conversation ID
        pendingAccessPointRef.current = { accessPoint, query };
        // If we already have a conversation ID, track immediately
        if (conversationState.conversationId) {
            const isNew = isNewConversationRef.current;
            trackConversationStart(accessPoint, query, isNew);
            pendingAccessPointRef.current = null;
        }
    }, [conversationState.conversationId, trackConversationStart]);
    // Get widget state and pending query
    const { isOpen } = widgetState;
    const { pendingQuery, pendingStacklineSku, pendingQueryId, clearPendingQuery } = useConciergePendingQuery();
    // Ref to track the last processed query ID to prevent duplicate sends
    const lastProcessedQueryIdRef = useRef(null);
    // Auto-scroll behavior
    const { chatWrapperRef, messagesEndRef, shouldScrollOnNextMessageRef, scrollToBottom } = useAutoScroll(conversationState.conversationHistory.length, conversationState.isActivelyStreaming, conversationState.streamingAIResponse, conversationState.conversationHistory.length === 0);
    // Handle stream errors - restore input and show appropriate toast
    const handleStreamError = useCallback((errorType, originalMessage) => {
        setSearchInputValue(originalMessage);
        const errorMessages = {
            rate_limit: 'Too many requests. Please wait a moment before trying again.',
            network: 'Network error. Please check your connection and try again.',
            server: 'Something went wrong on our end. Please try again.',
            unknown: 'Something went wrong. Please try again.',
        };
        toast.error(errorMessages[errorType]);
    }, []);
    // Streaming search - expose performSearch for direct calls from pending query handler
    const { performSearch, cancelStream } = useStreamingSearch({
        brandId,
        appConfigId,
        conversationState,
        scrollToBottom,
        shouldScrollOnNextMessageRef,
        onStreamError: handleStreamError,
    });
    // Handle search submission
    const handleSearch = useCallback((searchTerm, imageData, imageMimeType) => __awaiter(void 0, void 0, void 0, function* () {
        setSearchInputValue('');
        // Wrap search term with product context if on a PDP
        const searchTermWithContext = wrapSearchWithProductContext(searchTerm, pageContext);
        yield performSearch(searchTermWithContext, imageData, imageMimeType);
    }), [pageContext, performSearch]);
    // Handle update feedback
    const handleUpdateFeedback = (messageIndex, feedback) => {
        // Update the feedback in the conversation history
        conversationState.setConversationHistory((prev) => {
            return prev.map((msg, idx) => {
                if (idx === messageIndex) {
                    return Object.assign(Object.assign({}, msg), { feedback: feedback || undefined });
                }
                return msg;
            });
        });
    };
    // Conversation loader
    const { isLoadingConversationQuery } = useConversationLoader({
        conversationId: conversationState.conversationId,
        isActivelyStreaming: conversationState.isActivelyStreaming,
        conversationHistoryLength: conversationState.conversationHistory.length,
        apiBaseUrl: conversationState.apiBaseUrl,
        onConversationLoaded: (messages, productMap, surveyMap, followUpQuestions, previewSearchQueries, previewQueryProducts) => {
            conversationState.setProductMetadataMap(productMap);
            conversationState.setSurveyMetadataMap(surveyMap);
            conversationState.setConversationHistory(messages);
            conversationState.setFollowUpQuestions(followUpQuestions);
            conversationState.setPreviewSearchQueries(previewSearchQueries);
            conversationState.setPreviewQueryProducts(previewQueryProducts);
        },
        onConversationNotFound: () => {
            conversationState.setConversationId(null);
        },
    });
    // Sync conversationId with URL (disabled in widget)
    // useUrlSync(conversationState.conversationId);
    // Auto-send from URL params (disabled in widget)
    // useAutoSendFromUrl({
    //   isActivelyStreaming: conversationState.isActivelyStreaming,
    //   conversationLength: conversationState.conversationHistory.length,
    //   onSearch: handleSearch,
    // });
    // Handle pending queries from PDP conversation starters (Zustand-based - for backward compatibility)
    // This effect runs when: widget opens, or a new pending query is set
    useEffect(() => {
        // Skip if no pending query or widget not open
        if (!pendingQuery || !pendingQueryId || !isOpen) {
            return;
        }
        // Guard: Ensure performSearch is ready before processing
        if (!performSearch || typeof performSearch !== 'function') {
            return;
        }
        // Skip if we already processed this exact query (by ID)
        if (lastProcessedQueryIdRef.current === pendingQueryId) {
            return;
        }
        // Mark as processed immediately to prevent re-processing
        lastProcessedQueryIdRef.current = pendingQueryId;
        // Capture values before clearing
        const queryToSend = pendingQuery;
        const stacklineSkuToUse = pendingStacklineSku;
        // Clear the pending query immediately
        clearPendingQuery();
        // Wrap query with product context if we have a stacklineSku
        let messageToSend = queryToSend;
        if (stacklineSkuToUse) {
            const productPageContext = {
                pageType: 'pdp',
                stacklineSku: stacklineSkuToUse,
            };
            messageToSend = wrapSearchWithProductContext(queryToSend, productPageContext);
        }
        // Add a small delay to ensure the component is fully mounted and ready
        // This prevents crashes when the widget opens and immediately tries to process a query
        const timeoutId = setTimeout(() => {
            // Double-check that widget is still open and performSearch is still available
            if (!isOpen || !performSearch || typeof performSearch !== 'function') {
                // Reset the processed query ID so it can be retried later
                lastProcessedQueryIdRef.current = null;
                return;
            }
            // Send the message - performSearch handles adding user message and streaming response
            // Wrap in try-catch to prevent crashes if performSearch fails
            try {
                // Track conversation_starter access point (non-blocking)
                // This tracks PDP conversation starters (e.g., "Ask about this product" buttons)
                handleTrackAccessPoint('conversation_starter', queryToSend);
                performSearch(messageToSend);
            }
            catch (error) {
                console.error('Error processing pending query:', error);
                toast.error('Failed to process search. Please try again.');
                // Reset the processed query ID so it can be retried
                lastProcessedQueryIdRef.current = null;
            }
        }, 100); // Small delay to ensure component is fully mounted
        // Cleanup timeout if component unmounts or dependencies change
        return () => {
            clearTimeout(timeoutId);
        };
    }, [
        isOpen,
        pendingQuery,
        pendingQueryId,
        pendingStacklineSku,
        performSearch,
        clearPendingQuery,
        handleTrackAccessPoint,
    ]);
    // Handle initial query from props (props-based - for checkout-shop-app and PDP conversation starters)
    // This avoids cross-bundle Zustand store issues by using props instead
    const lastProcessedInitialQueryRef = useRef(null);
    const isProcessingInitialQueryRef = useRef(false);
    useEffect(() => {
        // Skip if no initialQuery or widget not open
        if (!initialQuery || !isOpen) {
            return;
        }
        // Guard: Ensure performSearch is ready before processing
        if (!performSearch || typeof performSearch !== 'function') {
            return;
        }
        // Skip if we already processed this exact query
        if (lastProcessedInitialQueryRef.current === initialQuery) {
            return;
        }
        // Skip if we're currently processing (timeout is pending)
        if (isProcessingInitialQueryRef.current) {
            return;
        }
        // Mark that we're starting to process (prevents duplicate timeouts)
        isProcessingInitialQueryRef.current = true;
        // Capture values before callback
        const queryToSend = initialQuery;
        const stacklineSkuToUse = initialQueryStacklineSku;
        // Wrap query with product context if we have a stacklineSku
        let messageToSend = queryToSend;
        if (stacklineSkuToUse) {
            const productPageContext = {
                pageType: 'pdp',
                stacklineSku: stacklineSkuToUse,
            };
            messageToSend = wrapSearchWithProductContext(queryToSend, productPageContext);
        }
        // Add a small delay to ensure the component is fully mounted and ready
        const timeoutId = setTimeout(() => {
            // Double-check that widget is still open and performSearch is still available
            if (!isOpen || !performSearch || typeof performSearch !== 'function') {
                // Reset so it can be retried later
                isProcessingInitialQueryRef.current = false;
                return;
            }
            // Send the message
            try {
                // Track conversation_starter access point for PDP pills
                handleTrackAccessPoint('conversation_starter', queryToSend);
                performSearch(messageToSend);
                // Mark as successfully processed (prevents re-processing same query)
                lastProcessedInitialQueryRef.current = queryToSend;
                // Notify parent that the query has been processed
                onInitialQueryProcessed === null || onInitialQueryProcessed === void 0 ? void 0 : onInitialQueryProcessed();
            }
            catch (error) {
                console.error('Error processing initial query:', error);
                toast.error('Failed to process search. Please try again.');
            }
            finally {
                isProcessingInitialQueryRef.current = false;
            }
        }, 100);
        return () => {
            clearTimeout(timeoutId);
            // Don't reset isProcessingInitialQueryRef here - let the effect re-run and decide
            // based on whether the query has actually been processed
            isProcessingInitialQueryRef.current = false;
        };
    }, [isOpen, initialQuery, initialQueryStacklineSku, performSearch, onInitialQueryProcessed, handleTrackAccessPoint]);
    // Render history view
    if (showHistory) {
        return (jsxs(Fragment, { children: [jsx(Toaster, {}), jsxs("div", Object.assign({ className: "concierge-root", style: {
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minHeight: 0,
                        maxHeight: '100%',
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                    } }, { children: [jsxs("div", Object.assign({ style: {
                                display: 'flex',
                                alignItems: 'center',
                                padding: '16px',
                                borderBottom: '1px solid #e5e7eb',
                                gap: '12px',
                                flexShrink: 0,
                            } }, { children: [jsx("button", Object.assign({ onClick: handleHistoryBack, style: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: 'transparent',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.15s ease',
                                    }, onMouseEnter: (e) => {
                                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                                    }, onMouseLeave: (e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }, "aria-label": "Back to chat" }, { children: jsx(ArrowLeft, { size: 20, color: "#031425" }) })), jsx("span", Object.assign({ style: {
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: '#031425',
                                        flex: 1,
                                    } }, { children: "Chat History" }))] })), jsx("div", Object.assign({ id: "new-conversation-button-container", style: {
                                padding: '12px 16px',
                                borderBottom: '1px solid #e5e7eb',
                                flexShrink: 0,
                                backgroundColor: '#ffffff',
                                position: 'relative',
                                zIndex: 10,
                            } }, { children: jsxs("button", Object.assign({ id: "new-conversation-button", onClick: handleNewConversation, type: "button", style: {
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    padding: '10px 16px',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#ffffff',
                                    color: '#031425',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    boxSizing: 'border-box',
                                }, onMouseEnter: (e) => {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                    e.currentTarget.style.borderColor = '#d1d5db';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                }, "aria-label": "Start new conversation" }, { children: [jsx(Plus, { size: 16, color: "#031425", strokeWidth: 2 }), jsx("span", { children: "New Conversation" })] })) })), jsx("div", Object.assign({ style: {
                                flex: 1,
                                overflow: 'hidden',
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                            } }, { children: jsx(HistoryList, { conversations: (conversationsData === null || conversationsData === void 0 ? void 0 : conversationsData.conversations) || [], isLoading: isLoadingConversations, error: conversationsError ? 'Failed to load conversations' : null, onSelect: handleHistorySelect, currentConversationId: conversationState.conversationId, widgetId: "concierge-widget-history", displayName: "Chat History", emptyMessage: "No conversations yet. Start chatting to see your history here." }) }))] }))] }));
    }
    return (jsxs(Fragment, { children: [jsx(Toaster, {}), jsxs("div", Object.assign({ className: "concierge-root", style: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 0,
                    maxHeight: '100%',
                    overflow: 'hidden',
                } }, { children: [jsx(ConciergeHeaderWidget, { showMaximize: showMaximize, isMaximized: isMaximized, onMaximize: onMaximize, onMinimize: onMinimize, onClose: widgetState.close, onHistoryClick: handleHistoryClick, title: title }), jsx(ConciergeChatAreaV2, { appName: config === null || config === void 0 ? void 0 : config.appName, brandId: brandId, conversationId: conversationState.conversationId, isEmbedded: true, apiBaseUrl: conversationState.apiBaseUrl, conversationHistory: conversationState.conversationHistory, productMetadataMap: conversationState.productMetadataMap, surveyMetadataMap: conversationState.surveyMetadataMap, streamingProductGroups: conversationState.streamingProductGroups, previewSearchQueries: conversationState.previewSearchQueries, previewQueryProducts: conversationState.previewQueryProducts, followUpQuestions: conversationState.followUpQuestions, streamingFeaturedProduct: conversationState.streamingFeaturedProduct, isActivelyStreaming: conversationState.isActivelyStreaming, streamingAIResponse: conversationState.streamingAIResponse, currentStatus: conversationState.currentStatus, isWaitingForFollowUpQueries: conversationState.isWaitingForFollowUpQueries, isLoadingConversationQuery: isLoadingConversationQuery, handleSearch: handleSearch, onUpdateFeedback: handleUpdateFeedback, onVariantSelect: onVariantSelect, onTrackAccessPoint: handleTrackAccessPoint, searchInputValue: searchInputValue, setSearchInputValue: setSearchInputValue })] }))] }));
};
// ConciergeWidgetChat expects to be inside a QueryClientProvider (provided by ConciergeStandalone)
const ConciergeWidgetChat = (props) => {
    return jsx(ConciergeWidgetChatContent, Object.assign({}, props));
};

export { ConciergeWidgetChat };
//# sourceMappingURL=ConciergeWidgetChat.js.map
